import { action, observable, makeObservable, computed } from 'mobx';
import Insrc from '@/utils/insrc';
import { querySelector, cache, isBroswer, isEmpty, getUserAccount } from '@/utils';
import { getProductCategory } from '@/utils/configs/product.config';
import { GlobalTracker } from '@/utils/Tracker';
import { httpRequest } from '@/request';
import { DataCollectBaseUrl, SERVER } from '@/request/config';
import { enableStaticRendering } from 'mobx-react';
import InstaStorage from '@/utils/libs/InstaStorage';
import { sha256 } from 'js-sha256';
import day from 'dayjs';
import TrackerQueue from '@/utils/Tracker/TrackerQueue';
import { armsErrorSend } from '@/utils/libs/armsErrorSend';
import { nanoid } from 'nanoid/non-secure';
import StoreBase from './Base';
import { getCamelUnderlineData } from '@/utils';
import { sendBeacon } from '@/request/sendBeacon';
import { omit } from 'lodash';

enableStaticRendering(typeof window === 'undefined');

class App extends StoreBase {
  constructor(props) {
    super(props);
    makeObservable(this);

    if (isBroswer) {
      this.insrc = new Insrc().getInsrc();
      window.insrc = new Insrc().getInsrc();
      this.instaStorage = InstaStorage;
      this.trackPageScrollPercentage();
      this.cloudSubscribeInfo = JSON.parse(cache.getSessionItem('cloud_subscribe_info') || '{}');
      const ua = navigator.userAgent;
      // 是否为平板设备，兼容新款ipad
      this.isTablet =
        // 检查设备是否具有粗指针和不支持悬停的屏幕特性，通常用于平板设备
        window.matchMedia('screen and (pointer: coarse) and (hover: none)').matches &&
        // 判断设备是否为iPad或非手机类的Android设备
        (/ipad|android(?!.*mobi)/i.test(ua) ||
          // 判断设备是否为新款iPad
          (ua.includes('Mac') && navigator.maxTouchPoints > 1));
      // 创建内部会话id
      this.handleSessionId({ action: 'set' });
    }
  }

  @observable
  campaign = {
    TEST_CAMPAIGN: null,
  };

  @observable
  insrc = '';

  @observable
  instaStorage = '';

  @observable
  isMobile = false;

  @observable
  isIOS = false;

  @observable
  isAndroid = false;

  @observable
  isApp = false;

  // 是否支持webp
  @observable
  webp = false;

  // pod节点环境变量 prod || dev || test || sit
  @observable
  PodEnv = '';

  // 商城跳转链接环境拓展，本地跳转到test
  @observable
  storeUrlExtend = '';

  // true: HZ节点， false: US节点
  @observable
  isCN = true;

  // 测试local环境
  @observable
  isLocal = '';

  // 生产环境
  @observable
  isProd = '';

  // 测试环境
  @observable
  isDev = '';

  // 测试环境
  @observable
  isSit = '';

  // 测试环境
  @observable
  isTest = '';

  // 页面完整路由
  @observable
  urlPathName = '';

  // 页面完整路由 + 参数
  @observable
  originalUrl = '';

  // 当前请求的完整路径 protocol + host + path
  @observable
  urlFull = '';

  // 映射到命名路由“parameters”的属性 e.g /product/:id -> req.params.id
  @observable
  urlParams = {};

  // 页面查询参数 ?key=val
  @observable
  urlQuery = {};

  // topbar是否显示
  @observable
  G_TopBarVisible = true;

  // 是否推广联盟推介
  @observable
  G_IsResellerIntro = false;

  // 是否隐藏导航栏、页脚
  @observable
  G_HideHeaderFooter = false;

  // 是否隐藏footer底部的订阅邮箱模块
  @observable
  G_HideFooterSubscribe = false;

  // 是否固定导航栏
  @observable
  G_PageMenusFixed = true;

  // 是否为产品页
  @observable
  G_IsProductPage = false;

  @observable
  G_IsHomePage = false;

  // 是否显示全局蒙层Loading
  @observable
  G_GlobalLoading = false;

  // 当前页面路由
  @observable
  G_CurrentPathName = isBroswer ? window.location.pathname.toLowerCase() : '/';

  @observable
  isProductPage = false;

  @observable
  isCartPage = false;

  @observable
  isCloudUpgradeOrderConfirmPage = false;

  @observable
  isOrderConfirmPage = false;

  @observable
  isOrderResultPage = false;

  @observable
  isOrderPayPage = false;

  @observable
  isSubscriptionPage = false;

  // 服务端返回通过redis配置的活动信息
  @observable
  campaignInfo = {};

  // 服务端返回通过redis配置的页面公告配置
  @observable
  pageCommonConfig = {
    productRepairStockSales: {},
    ModularOverviewCid: [], // 自定义显示模块化详情的套餐id序列
    DefaultOverviewCid: [], // 自定义显示默认详情的套餐id序列
    NoneOverviewCid: [], // 自定义不显示详情的套餐id序列
  };

  @observable
  InstaUserActive = false;

  // 用户权益详情
  @observable
  cloudSubscribeInfo = {};

  @action
  updateCloudSubscribeInfo(data) {
    this.cloudSubscribeInfo = data;
    if (!isEmpty(data)) {
      cache.setSessionItem('cloud_subscribe_info', JSON.stringify(data));
    } else {
      cache.removeSessionItem('cloud_subscribe_info');
    }
  }

  // 是否激活udesk客服,
  @observable
  showUDesk = ''; // true || false, 未初始化则为''，不展示任何客服入口

  // 用户是否在权益期
  @computed
  get isCloudServiceActive() {
    return this.cloudSubscribeInfo?.benefitStatus === 'active';
  }

  // 是否是八折权益用户
  @computed
  get hasAccessoriesDiscount() {
    return !isEmpty(this.cloudSubscribeInfo?.discountBenefitInfo);
  }

  @computed
  get productRepairStockList() {
    let list = [];
    try {
      const { productRepairStockSales } = this.pageCommonConfig;
      list = [
        ...(productRepairStockSales?.one_x2?.cids ?? []),
        ...(productRepairStockSales?.go2?.cids ?? []),
        ...(productRepairStockSales?.go3?.cids ?? []),
        ...(productRepairStockSales?.one_rs?.cids ?? []),
      ];
      return list;
    } catch (e) {
      return [];
    }
  }

  /**
   *
   * @description 获取页面是否存在销售库存的商品，若当前页面含有且满足条件，则隐藏paypal支付、pay later msg 或者显示分包发货信息；
   * @date 2023/06/01 15:16:46
   * @author haifn
   * @param
   * @return
   */
  @computed
  get isFilterRepairStock() {
    try {
      const { productCarts } = this.root.product;
      const { selectedCartItems, orderCartItems, simpleDetailOrder } = this.root.order;

      // 产品页若当前选中过滤套餐，则隐藏pay later msg
      if (this.isProductPage && productCarts.length) {
        const filterCommodity = productCarts.filter((item) =>
          this.productRepairStockList.find((cid) => item.id === cid)
        );
        if (filterCommodity.length) return true;
      }

      // 购物车若含有过滤套餐，且包含其他非过滤套餐的情况下：
      // 需要隐藏pay later msg
      // 需要显示分包发货弹窗
      if (this.isCartPage && selectedCartItems.length) {
        const filterCommodity = selectedCartItems.filter((item) =>
          this.productRepairStockList.find((cid) => item.id === cid || item.commodityId === cid)
        );
        if (filterCommodity.length) return true;
      }

      // 结账页若含有过滤套餐，则需要隐藏paypal支付；
      if (this.isOrderConfirmPage && orderCartItems.length) {
        const filterCommodity = orderCartItems.filter((item) =>
          this.productRepairStockList.find((cid) => item.id === cid || item.commodityId === cid)
        );
        if (filterCommodity.length) return true;
      }

      // 二次支付页，移动端支付结果(失败)页，订单若含有过滤套餐，则需要隐藏paypal支付；
      if ((this.isOrderPayPage || this.isOrderResultPage) && simpleDetailOrder?.orderItemInfos?.length) {
        const filterCommodity = simpleDetailOrder?.orderItemInfos.filter((item) =>
          this.productRepairStockList.find((cid) => item.commodityId === cid)
        );
        if (filterCommodity.length) return true;
      }

      return false;
    } catch (e) {
      return false;
    }
  }

  @observable
  GlobalVideoPopPlayer = {
    src: '',
    poster: '',
    closeCallback: () => {},
    trackPlayTimeParams: {
      category: '',
      action: '',
      label: '',
      value: '',
    },
  };

  @observable
  CookieConsentInstance = {};

  @action
  setGlobalValue = (key, value) => {
    this[key] = value;
  };

  /**
   * @description 临时、持久化存储会话id，用于数据上报，存储在localStorage
   * @param {String} action set || get
   */
  @action
  handleSessionId = ({ action = 'set' }) => {
    const key = '_ins_sid';
    let _ins_sid = cache.get(key);
    const nanoid10 = nanoid(10);

    switch (action) {
      case 'get':
        return _ins_sid ?? 'not set';
      case 'set':
        if (!_ins_sid) {
          const val = nanoid10;
          cache.set(key, val);
        }

        break;
      default:
    }
  };

  /**
   * @description 获取用户云服务权益信息
   * @param from loginCallback 来自于登陆回调（走缓存优先，只请求一次） ｜ from其他的页面则需要刷新页面数据，直接调用
   */
  @action
  getCloudSubscribeDetail = async (from) => {
    const dispatchLoginCb = () => {
      !!from &&
        window.dispatchEvent(
          new CustomEvent('loginCbSubscribeDetail', {
            detail: this.isCloudServiceActive,
          })
        );
    };

    if (from === 'loginCallback' && cache.getSessionItem('cloud_subscribe_init')) return dispatchLoginCb();
    cache.setSessionItem('cloud_subscribe_init', 1);

    return httpRequest({
      url: SERVER.API_SERVER_BASE + '/cloud/getCloudStorageSubscribeInfo',
      method: 'get',
      instaToken: true,
    })
      .then((res) => {
        // benefitStatus-用户云存储权益状态 active:正常 expired:过期
        if (res.code === 0 && !isEmpty(res.data)) {
          res.data.subscribeDetail = res.data.cloudSubscribeInfo; // 重名处理。

          this.updateCloudSubscribeInfo({
            ...omit(this.cloudSubscribeInfo, 'discountBenefitInfo', 'extendBenefitInfo', 'careBenefitInfo'),
            ...(res.data || {}),
          });
        } else {
          this.updateCloudSubscribeInfo({});
        }

        dispatchLoginCb();
        return res;
      })
      .catch(() => {
        dispatchLoginCb();
        this.updateCloudSubscribeInfo({});
      });
  };

  /**
   * @description 获取用户是否有升级资格
   * code码文档：https://arashivision.feishu.cn/wiki/CpEzw0ntPiY24EkCWf8c815EnPd#share-ACr3dilOLoeEe6xKHq9cNiOMnOf
   */
  @action
  verifyUpgradeQualification = () => {
    const { instaToken } = this.root.auth;
    if (!instaToken) return;

    return httpRequest({
      url: SERVER.API_SERVER_CLOUD + '/verifyUpgradeQualification',
      instaToken: true,
    })
      .then((res) => {
        if (res.code === 0) {
          const { businessCode, canUpgrade, ...restProps } = res.data;
          this.cloudSubscribeInfo.upgradeInfo = {
            /** 是否有升级资格 */
            canUpgrade,
            // canUpgrade: false,
            /** 非官方商城订阅 */
            noStoreChannel: businessCode === 105001001,
            /** 没有开启自动续费 */
            noAutoRenew: businessCode === 105001002,
            /** 存在未支付的升级订单 */
            existUpgradeOrder: businessCode === 105001003,
            /** 升级地区与当前生效中订阅地区不一致 */
            upgradeRegionNotMatch: businessCode === 105001004,
            /** 当前订阅套餐不可升级 */
            noUpgradeCommodity: businessCode === 105001005,
            /** 存在当前生效订阅的 */
            existRmaOrder: businessCode === 105001006,
            /** 不符合订阅到期前48小时以上 */
            noSubscriptionExpire: businessCode === 105001007,
            ...restProps,
          };
          this.updateCloudSubscribeInfo(this.cloudSubscribeInfo);
        } else {
          this.cloudSubscribeInfo.upgradeInfo = {};
          this.updateCloudSubscribeInfo(this.cloudSubscribeInfo);
        }
        return this?.cloudSubscribeInfo?.upgradeInfo;
      })
      .catch((e) => {
        console.error(e);
        this.cloudSubscribeInfo.upgradeInfo = {};
        this.updateCloudSubscribeInfo(this.cloudSubscribeInfo);
      });
  };

  /**
   * @description 得知用户是否在订阅期。
   * 如果已有详情在订阅期->接口返回false，清除详情；->接口返回true，覆盖。
   * 如果没有详情在订阅期->接口返回false，不做处理；->返回true，赋值。
   */
  @action
  checkUserSubscriptionState = () => {
    return httpRequest({
      url: SERVER.API_SERVER_CLOUD + '/cloudSubscribeUserCheck',
      instaToken: true,
    })
      .then((res) => {
        // benefitStatus-用户云存储权益状态 active:正常 expired:过期
        if (res.code === 0 && !isEmpty(res.data)) {
          if (res.data.subscribed) {
            this.cloudSubscribeInfo.benefitStatus = 'active';
            this.updateCloudSubscribeInfo(this.cloudSubscribeInfo);
          } else {
            this.updateCloudSubscribeInfo({});
          }
        } else {
          this.updateCloudSubscribeInfo({});
        }

        return res;
      })
      .catch(() => {
        this.updateCloudSubscribeInfo({});
      });
  };

  /*
   *
   * @desc: 上报页面浏览百分比
   * @author: haifn
   * @time: 2021/7/8 11:20 上午
   *
   */
  @action
  trackPageScrollPercentage = async () => {
    const record = {
      path: '/',
      view: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
    };
    const gHeader = await querySelector('#g-header');
    const gHeaderHeight = gHeader.clientHeight;

    function calcPercentage(viewPercentage) {
      viewPercentage = parseInt(viewPercentage, 10);
      if (viewPercentage < 10) {
        return 0;
      }
      if (viewPercentage >= 100) {
        return 100;
      }
      return viewPercentage.toString()[0] * 10;
    }

    window.addEventListener('scroll', () => {
      // 初始化浏览记录
      if (record.path !== this.G_CurrentPathName) {
        record.path = this.G_CurrentPathName;
        record.view = [];
      }

      // 页面总高，导航固定的情况下需要把导航高度加上
      let totalHeight = document.body.scrollHeight || document.documentElement.scrollHeight;
      if (this.G_PageMenusFixed) {
        totalHeight += gHeaderHeight;
      }
      // 视区高度
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      // 可滚动高度
      const validScrollHeight = totalHeight - windowHeight;
      // 滚动条卷去高度
      const scrollHeight = window.scrollY || window.pageYOffset;
      // 计算滚动百分比
      const viewPercentage = parseInt((scrollHeight / validScrollHeight) * 100, 10);

      const percentage = calcPercentage(viewPercentage);
      if (!record.view.includes(percentage)) {
        record.view.push(percentage);
        GlobalTracker.pageViewPercentage({
          action: 'view',
          percentage,
        });
      }
    });
  };

  /*
   *
   * @desc   上报百度广告转化
   * @param {int} newType 46,加入购物车 || 10,购买成功
   * @time  2021/7/22 6:17 下午
   *
   */
  @action
  trackBaiduAdv = ({ newType }) => {
    if (typeof window === 'undefined') return;
    const logidUrl = cache.getCookie('bd_vid_url');
    const data = {
      platform: 'store',
      logidUrl,
      newType,
    };
    if (!data.logidUrl) return;
    if (!window?.CookieConsentInstance) {
      return TrackerQueue.push('BaiduAds', this.trackBaiduAdv, [{ newType }], this);
    }
    if (!window?.CookieConsentInstance?.canLoadSnippets('BaiduAds')) return;
    httpRequest({
      url: DataCollectBaseUrl + `/baidu_forward`,
      method: 'post',
      data,
    });
  };

  /**
   * @desc  facebook转化 通过服务器和客户端两种方式进行上报。屏蔽测试环境的数据上报
   * @param category 上报事件
   * @param values 额外使用信息 常用data、delivery为key
   * 参考：
   * fbq方法使用： https://developers.facebook.com/docs/meta-pixel/implementation/conversion-tracking/
   * user-data信息参数说明：https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/customer-information-parameters
   * 去重事件说明：
   * https://www.facebook.com/business/help/308855623839366?id=818859032317965
   * https://developers.facebook.com/docs/marketing-api/conversions-api/deduplicate-pixel-and-server-events/
   */
  @action
  trackFb = (category, values = {}) => {
    if (typeof window === 'undefined' || !window.isProd) return;

    const countryCode = cache.getCookie('X_Country_Code') || 'US';
    if (countryCode === 'CN') return;

    if (!window?.CookieConsentInstance || !window.fbq) {
      return TrackerQueue.push('FacebookPixel', this.trackFb, [category, values], this);
    }
    if (!window?.CookieConsentInstance?.canLoadSnippets('FacebookPixel')) return;

    try {
      const { data = {}, delivery } = values;
      // FaceBook点击ID存储在_fbc您域下的浏览器cookie中
      const fbc = cache.getCookie('_fbc');
      const fbp = cache.getCookie('_fbp');
      const PIXEL_ID = 451591722489720;

      const { account = {} } = this.root.auth;
      const categoryToType = {
        AddToCart: 1,
        PAGEVIEW_EVENT: 12,
        ViewContent: 14,
        Purchase: 22,
        InitiateCheckout: 13,
      };
      let payload = {};
      let fbPixelPayload = {};
      let fbPixelUserdata = {}; // 像素方式userData信息
      let fbAPIUserdata = {}; // API方式userData信息

      const initUserData = () => {
        const _delivery = delivery ?? data;

        fbAPIUserdata = {
          email:
            fbAPIUserdata.email || (account ? account.email : '') || _delivery.email || _delivery.contactEmail || '',
          phone: _delivery.phone ? _delivery.phone_code ?? _delivery.phoneCode + ' ' + _delivery.phone : '',
          firstName: _delivery.first_name
            ? String(_delivery.first_name).toLowerCase()
            : account
            ? account.first_name
            : '',
          lastName: _delivery.last_name ? String(_delivery.last_name).toLowerCase() : account ? account.last_name : '',
          city: _delivery.city,
          state: _delivery.province,
          zipCode: _delivery.zip_code,
        };

        // 后端接口用户参数
        payload.order_contact_email = fbAPIUserdata.email;

        // 像素事件用户参数
        fbPixelUserdata = {
          em: fbAPIUserdata.email,
          fn: fbAPIUserdata.lastName,
          ln: fbAPIUserdata.firstName,
          ph: fbAPIUserdata.phone,
          ct: fbAPIUserdata.city,
          st: fbAPIUserdata.state,
          zp: fbAPIUserdata.zipCode,
          db: account ? account.birthday?.replace(/-/gi, '') ?? '' : '',
          country: countryCode.toLowerCase(),
        };

        if (account) window.fbq && window.fbq('init', PIXEL_ID, fbPixelUserdata);
      };
      initUserData();

      // 如果有传快递信息，需要传输给后端地址信息的参数，目前只有购买后的埋点有
      if (['InitiateCheckout', 'Purchase'].includes(category)) {
        const _delivery = delivery ?? data;

        payload.order_delivery_last_name = getCamelUnderlineData(_delivery, 'last_name');
        payload.order_delivery_first_name = getCamelUnderlineData(_delivery, 'first_name');
        payload.order_delivery_phone = getCamelUnderlineData(_delivery, 'phone');
        payload.order_delivery_phone_code = getCamelUnderlineData(_delivery, 'phone_code');
        payload.order_delivery_city = getCamelUnderlineData(_delivery, 'city');
        payload.order_delivery_zip_code = getCamelUnderlineData(_delivery, 'zip_code');
        payload.order_delivery_province = getCamelUnderlineData(_delivery, 'province');
        payload.order_delivery_country_code = _delivery.country_code || _delivery.area;
      }

      switch (category) {
        case 'PAGEVIEW_EVENT':
          break;
        case 'ViewContent': // 进入产品页浏览
          payload = { ...values };
          fbPixelPayload = {
            content_type: 'product',
            content_ids: [`${values.productId}`],
          };
          break;
        case 'AddToCart':
          payload.productId = values.productId;
          payload.orderItemCommodityList = [
            {
              commodityId: values.commodityId,
              number: values.count,
              serviceItemList: values.bind_services?.map((d) => ({
                id: d.id,
                number: d.number,
              })),
            },
          ];
          payload.total_price_amount = values.price.amount * values.count;
          payload.total_price_currency = values.price.currency;
          payload.commodity_price_amount = values.price.amount;
          payload.commodity_price_currency = values.price.currency;
          payload.commodity = values.commodityId;

          // 像素方式
          fbPixelPayload = {
            content_ids: ['mall-' + values.commodityId],
            content_type: 'product',
            content_name: values.name + ' ' + values.commodity.info.name,
            value: values.price.amount * values.count,
            currency: values.price.currency,
          };
          break;
        case 'InitiateCheckout': // 结算确认页的创建订单
          payload.orderId = values.order_number;
          payload.order_get_order_number = values.order_number;
          payload.order_items_get_product_list = data.map((d) => d.commodity.id);

          payload.order_payment_total_pay_price_amount = values.total;
          payload.order_payment_total_pay_price_currency = data[0]?.price?.currency ?? undefined;
          // 像素方式处理
          fbPixelPayload = {
            content_ids: data.map((d) => d.commodity.id),
            content_type: 'product',
            content_name: data.map((d) => d.name + '-' + d.commodity.info.name).join(' , '),
            value: values.total,
            currency: data[0]?.price?.currency ?? undefined,
            order_id: values.order_number,
            contents: data.map((d) => ({
              id: d.commodity.id,
              quantity: d.count,
            })),
          };

          break;
        case 'Purchase': // 当用户完成支付到达成功支付页进行数据回传
          const { orderItemInfos, orderPaymentInfo, orderNumber } = data;
          payload.orderId = data.orderNumber;
          payload.order_get_order_number = data.orderNumber;
          payload.order_items_get_product_list = orderItemInfos.map((d) => d.commodityId);
          payload.order_payment_total_pay_price_amount = orderPaymentInfo.totalAmount;
          payload.order_payment_total_pay_price_currency = orderPaymentInfo.currency;

          // 像素方式
          fbPixelPayload = {
            content_ids: orderItemInfos.map((d) => d.commodityId),
            content_type: 'product',
            content_name: orderItemInfos.map((d) => d.productName).join(' , '),
            value: orderPaymentInfo.totalAmount,
            currency: orderPaymentInfo.currency,
            order_id: orderNumber,
            contents: orderItemInfos.map((d) => ({
              id: d.commodityId,
              quantity: d.number,
            })),
          };
          break;
        default:
          break;
      }
      const eventID = payload.orderId
        ? payload.orderId.substr(3)
        : Math.random().toString(16).substring(3) + Date.now().toString(36);

      window.fbq &&
        window.fbq('track', category, fbPixelPayload, {
          eventID,
          value: fbPixelPayload.value,
          currency: fbPixelPayload.currency,
        });

      sendBeacon({
        url: DataCollectBaseUrl + `/facebook/conversionsDataReporting`,
        data: {
          ...payload,
          ...fbAPIUserdata,
          eventId: eventID,
          eventType: categoryToType[category],
          eventSourceURL: document.URL,
          clientUserAgent: navigator.userAgent,
          countryCode,
          fbc,
          fbp,
        },
      });
    } catch (e) {
      armsErrorSend(e, { filename: 'BI- FB', data: { category, values } });
    }
  };

  /**
   * @desc  tiktok转化 直接上报数据至tiktok数据追踪。屏蔽测试环境的数据上报
   * @param event 事件名称
   * @param values 事件相关参数
   * Pixel名称：store，Pixel ID: 7061780290276048897, pixel_code: C8089ITGL1ARI64QSFBG
   * AccessToken 023078af33c29972c70740f09446abb04a51722c
   * 广告主ID：7049941623597187074，app_id: 7062385370503053314，secret: c9b71809b3976e4629c3bdf68605ee1aed7ac34d
   *
   * 结合事件API+标准像素方式：
   * https://ads.tiktok.com/help/article?aid=10012410&redirected=1
   * https://ads.tiktok.com/help/article/events-api?lang=zh
   * Pixel方式参数：https://ads.tiktok.com/marketing_api/docs?id=1739585702922241
   * 事件API参数：https://ads.tiktok.com/marketing_api/docs?id=1741601162187777
   */
  trackTiktok = (event, values = {}) => {
    if (typeof window === 'undefined' || !window.isProd) return;

    const pixel_code = 'C8089ITGL1ARI64QSFBG';
    if (!window?.CookieConsentInstance || !window.ttq) {
      return TrackerQueue.push('TikTok', this.trackTiktok, [event, values], this);
    }
    if (!window?.CookieConsentInstance?.canLoadSnippets('TikTok')) return;

    try {
      const { data = {}, order_number, total, delivery = {} } = values;
      const { email, guest } = this.root.auth;
      const account = getUserAccount();

      const { country = {} } = this.root.area ?? {};
      const { curSelectCommodityCount, curSelectCommodity, productDetail } = this.root.product ?? {};

      let properties = {};
      let event_properties = {};

      // 填充身份数据
      const userData = {
        phone_number: account?.phone
          ? `+${account.phone_code || ''}${account.phone}`?.replace('++', '+')
          : data.phone
          ? `+${data.phoneCode || ''}${data.phone}`?.replace('++', '+')
          : delivery?.phone
          ? `+${delivery.phone_code || ''}${delivery.phone}`?.replace('++', '+')
          : '',
        email: account.email || email || guest || '',
      };
      if (isEmpty(userData.phone_number)) userData.phone_number = '';
      if (isEmpty(userData.email)) userData.email = '';

      window.ttq?.identify(userData);
      let event_id = order_number || `store${Math.random().toString(16).substring(3) + Date.now().toString(36)}`;

      switch (event) {
        case 'ViewContent':
          properties = {
            content_type: 'product',
            content_id: curSelectCommodity.id,
            content_category: getProductCategory(productDetail),
            content_name: productDetail.info?.name + ' ' + curSelectCommodity.info.name,
            currency: country.currency,
            value: curSelectCommodity.price.amount,
            price: curSelectCommodity.price.amount,
            quantity: curSelectCommodityCount,
          };
          event_properties = {
            content_type: 'product_group',
            contents: [
              {
                content_type: 'product',
                content_id: curSelectCommodity.id,
                content_category: getProductCategory(productDetail),
                content_name: productDetail.info?.name + ' ' + curSelectCommodity.info.name,
                price: curSelectCommodity.price.amount,
                quantity: curSelectCommodityCount,
              },
            ],
            currency: country.currency,
            value: curSelectCommodity.price.amount,
          };
          break;
        case 'AddToCart':
          properties = {
            content_type: 'product_group',
            contents: [
              {
                content_type: 'product',
                price: values.price.amount,
                quantity: values.count,
                content_id: values.commodity?.id || values.commodityId,
                content_category: getProductCategory(values.productId),
                content_name: values.name + ' ' + values.commodity.info.name,
              },
              ...(values.bind_services?.map(
                (item) =>
                  item && {
                    content_type: 'product',
                    price: item.price.amount,
                    quantity: values.count,
                    content_id: item.id,
                    content_category: 'service',
                    content_name: values.name + '-' + item.type,
                  }
              ) ?? []),
            ],
            currency: values.price.currency,
            value: values.price.amount,
          };
          event_properties = properties;
          break;
        case 'InitiateCheckout':
        case 'PlaceAnOrder':
          const contents = data.slice(0);
          data.forEach((item) => {
            const services = item.bind_order_items || item.bind_services;
            if (services) {
              services.forEach((d) => {
                d.commodity = d.commodity || { id: d.id };
              });
              contents.push(...services);
            }
          });
          properties = {
            content_type: 'product_group',
            contents: contents.map((item) => ({
              content_type: 'product',
              price: item.price?.amount || item.price,
              quantity: item.count || item.number,
              content_id:
                item.commodity?.id || item.commodity || item.product_id || item.productId || item.product || item.id,
              content_category: getProductCategory(item),
              content_name:
                (item.product_name || item.name || '') + ' ' + (item.info?.name || item.commodity_name || ''),
            })),
            currency: country.currency,
            value: total,
            // description: order_number,
          };
          event_properties = properties;
          break;
        case 'CompletePayment':
          const { data: simpleOrderInfo } = values;
          properties = {
            content_type: 'product_group',
            contents: simpleOrderInfo.orderItemInfos.map((item) => ({
              content_type: 'product',
              price: item.amount,
              quantity: item.number,
              content_id: item.commodityId,
              content_category: getProductCategory(item),
              content_name: `${item.productName} ${item.commodityName}`,
            })),
            currency: simpleOrderInfo.orderPaymentInfo.currency,
            value: simpleOrderInfo.orderPaymentInfo.totalAmount,
          };
          event_properties = properties;
          break;
        default:
          break;
      }

      window.ttq?.track(event, properties, { event_id });

      sendBeacon({
        url: DataCollectBaseUrl + `/tiktok/eventPush`,
        data: {
          pixel_code,
          event,
          event_id,
          properties: event_properties,
          timestamp: day().toISOString(),
          context: {
            ad: {
              callback: cache.getCookie('ttcid'),
            },
            page: {
              url: document.location.href,
              referrer: document.referrer,
            },
            user: {
              ttp: cache.getCookie('_ttp'),
              external_id: sha256(`${account?.id ?? ''}`),
              phone_number: !isEmpty(userData.phone_number) ? sha256(userData.phone_number) : '',
              email: !isEmpty(userData.email) ? sha256(userData.email?.toLowerCase()) : '',
            },
            user_agent: navigator.userAgent,
          },
        },
      });
    } catch (e) {
      armsErrorSend(e, { filename: 'BI- tiktok', data: { event, values } });
    }
  };

  /**
   * @desc  Twitter转化 o1op9(日本)  obvge(全球)
   * @param category
   * @param values 额外使用信息 常用data、label为key
   * https://business.twitter.com/zh-cn/help/campaign-measurement-and-analytics/conversion-tracking-for-websites.html
   */
  @action
  trackTwitter = (category, values = {}) => {
    if (typeof window === 'undefined') return;

    const { data } = values;
    const { productTotalPrice = {} } = this.root.product ?? {};
    const { email, guest } = this.root.auth ?? {};
    const account = getUserAccount();
    const countryCode = cache.getCookie('X_Country_Code') || 'US';
    if (!window?.CookieConsentInstance || !window.twq) {
      return TrackerQueue.push('Twitter', this.trackTwitter, [category, values], this);
    }
    if (!window?.CookieConsentInstance?.canLoadSnippets('Twitter')) return;

    try {
      const categoryNumberJP = {
        AddToCart: 'tw-o1op9-obw1q',
        Purchase: 'tw-o1op9-obw1z',
      };
      const categoryNumber = {
        AddToCart: 'tw-obvge-of0fl',
        Purchase: 'tw-obvge-obvhd',
      };
      let payload = {
        phone_number: !isEmpty(account?.phone) ? `+${account.phone_code || ''}${account.phone}` : '',
        email_address: email || guest || values.contact_email,
      };

      switch (category) {
        case 'Purchase': // 结算确认页提交订单
          payload = {
            ...payload,
            email_address: values.contactEmail,
            phone_number: `${values.phoneCode}${values.phone}`,
            value: values.orderPaymentInfo.totalAmount,
            currency: values.orderPaymentInfo.currency,
            conversion_id: values.orderNumber,
            contents: values.orderItemInfos.map((item) => {
              return {
                content_id: item.commodityId,
                num_items: item.number,
                content_price: item.amount * item.number,
                content_name: `${item.productName} ${item.commodityName}`,
              };
            }),
          };

          break;
        case 'AddToCart':
          const { commodity, bind_services = [], count, name = '', price = {} } = data;
          payload = {
            ...payload,
            value: productTotalPrice.amount ? productTotalPrice.amount : price.amount,
            currency: price.currency ?? productTotalPrice.currency,
            contents: [
              {
                content_id: commodity.id,
                num_items: count,
                content_price: commodity.price.amount * count,
                content_name: name + '-' + commodity.info.name,
              },
              ...(bind_services?.length > 0
                ? bind_services.map((d) => ({
                    content_id: d.id,
                    num_items: d.number,
                    content_price: d.price.amount * count,
                    content_name: name + '-' + d.type,
                  }))
                : []),
            ],
            // conversion_id: 唯一性转化id，暂时不使用
          };

          break;
        default:
          break;
      }
      window.twq('event', categoryNumber[category], payload);
      if (countryCode === 'JP') {
        window.twq('event', categoryNumberJP[category], payload);
      }
    } catch (e) {
      armsErrorSend(e, { filename: 'BI- Twitter', data: { category, values } });
    }
  };

  /**
   *
   * @description 全局弹窗相关顺序逻辑控制，均在cookiebot合规弹窗之后；
   */
  @observable
  GlobalModalQueue = [
    {
      key: 'CookieBot',
      activated: false, // 是否已激活
    },
    // {
    //   key: 'NpsMeter',
    //   activated: false,
    // },
    {
      key: 'UserSubscribeModal',
      activated: false,
    },
  ];

  @action
  setGlobalModalState = (modalData = {}) => {
    const {
      country: { X_Language },
    } = this.root.area;
    const curModal = this.GlobalModalQueue.find((o) => o.key === modalData.key);
    const NpsMeterModal = this.GlobalModalQueue.find((o) => o.key === 'NpsMeter');
    const UserSubscribeModal = this.GlobalModalQueue.find((o) => o.key === 'UserSubscribeModal');

    if (!isBroswer || !curModal) return;

    curModal.activated = modalData.activated;

    if (modalData.key === 'CookieBot') {
      // 限制条件激活弹窗
      if (!!NpsMeterModal && (['/'].includes(this.urlPathName) || ['en_us', 'ja_jp'].includes(X_Language))) {
        NpsMeterModal.activated = true;
      }

      // 默认激活订阅弹窗
      UserSubscribeModal.activated = true;
    }
    this.setGlobalValue('GlobalModalQueue', this.GlobalModalQueue);
  };

  @computed
  get curGlobalActivatedModal() {
    if (!isBroswer) return {};
    const result = {};
    this.GlobalModalQueue.forEach((o) => {
      if (o.activated) {
        result[o.key] = true;
      }
    });
    return result;
  }
}

export default App;
